"use client";

import React, { useMemo } from "react";
import Reveal from "@/components/common/Reveal";
import { ArrowRight, FileSpreadsheet } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import DataTable, { type DataTableColumn } from "@/components/common/DataTable";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { type L6 } from "@/lib/commonCopy";
interface SpecRow {
  weaveType: L6;
  micronRating: string;
  primaryBenefit: L6;
  linkTo: string;
}
const technicalSpecs: SpecRow[] = [{
  weaveType: {
    en: "Plain Dutch Weave Wire Mesh",
    zh: "平纹荷兰编织丝网",
    es: "Malla de alambre de tejido Dutch liso",
    de: "Glatte Tresse Drahtgewebe",
    fr: "Toile métallique tissage hollandais uni",
    pt: "Malha de arame de trama holandesa lisa"
  },
  micronRating: "10 µm – 100 µm",
  primaryBenefit: {
    en: "High mechanical strength with low pressure drop across high-velocity gas streams",
    zh: "高流速气体工况下具备高机械强度与低流阻压降",
    es: "Alta resistencia mecánica con baja caída de presión en corrientes de gas de alta velocidad",
    de: "Hohe mechanische Festigkeit mit geringem Druckverlust bei hohen Gasgeschwindigkeiten",
    fr: "Haute résistance mécanique avec faible perte de charge sous flux de gaz à grande vitesse",
    pt: "Alta resistência mecânica com baixa queda de pressão em correntes de gás de alta velocidade"
  },
  linkTo: "/PlainDutchWeavePage"
}, {
  weaveType: {
    en: "Twilled Dutch Weave Wire Mesh",
    zh: "斜纹荷兰编织丝网",
    es: "Malla de alambre de tejido Dutch cruzado",
    de: "Köpertresse Drahtgewebe",
    fr: "Toile métallique tissage hollandais sergé",
    pt: "Malha de arame de trama holandesa sarja"
  },
  micronRating: "1 µm – 25 µm",
  primaryBenefit: {
    en: "Tight tortuous flow path for sub-micron particulate separation and fine gas mist",
    zh: "曲折深层过滤通道，适用于亚微米级颗粒截留与气相微雾分离",
    es: "Canal tortuoso cerrado para separación de partículas submicrónicas y niebla de gas fina",
    de: "Enge gewundene Strömungswege für submikrone Partikelabscheidung und feine Gasnebel",
    fr: "Cheminement tortueux étroit pour la séparation de particules submicroniques et brouillard de gaz fin",
    pt: "Caminho tortuoso fechado para separação de partículas submicrométricas e névoa fina de gás"
  },
  linkTo: "/TwillDutchWeavePage"
}, {
  weaveType: {
    en: "5-Layer Sintered Wire Mesh",
    zh: "5层标准烧结网复合板",
    es: "Malla de alambre sinterizada de 5 capas",
    de: "5-lagiges Sinterdrahtgewebe",
    fr: "Toile métallique frittée 5 couches",
    pt: "Malha de arame sinterizada de 5 camadas"
  },
  micronRating: "2 µm – 200 µm",
  primaryBenefit: {
    en: "Rigid structure eliminating media migration, fully pulse-jet cleanable",
    zh: "结构坚固绝无滤材脱落，耐受反吹脉冲在线清洗",
    es: "Estructura rígida que elimina el desprendimiento de fibras, lavable por pulso de aire",
    de: "Starre Struktur ohne Medienabrieb, vollständig per Pulse-Jet abreinigbar",
    fr: "Structure rigide éliminant la migration de fibres, nettoyable par décolmatage pneumatique",
    pt: "Estrutura rígida que elimina desprendimento de meio, totalmente lavável por pulso de ar"
  },
  linkTo: "/SinteredMultilayerMeshPage"
}, {
  weaveType: {
    en: "310S High-Temperature Woven Mesh",
    zh: "310S 耐高温编织丝网",
    es: "Malla tejida para alta temperatura 310S",
    de: "Hochtemperaturbeständiges Drahtgewebe 310S",
    fr: "Toile tissée haute température 310S",
    pt: "Malha tecida para alta temperatura 310S"
  },
  micronRating: "25 µm – 250 µm",
  primaryBenefit: {
    en: "Exceptional resistance to oxidation and cyclic thermal stress in furnace exhausts",
    zh: "高温炉气废气工况下具备抗热循环冲击与优异抗氧化性能",
    es: "Excepcional resistencia a la oxidación y estrés térmico cíclico en gases de escape de hornos",
    de: "Hervorragende Oxidations- und Temperaturwechselbeständigkeit in Ofenabgasen",
    fr: "Résistance exceptionnelle à l'oxydation et aux contraintes thermiques cycliques dans les fumées de four",
    pt: "Excepcional resistência à oxidação e estresse térmico cíclico em exaustão de fornos"
  },
  linkTo: "/SpecialtyWovenMeshPage"
}];
const copy = {
  matrixTitle: {
    en: "Gas Filtration Wire Mesh Selection Matrix",
    zh: "气体过滤丝网技术规格选型表",
    es: "Matriz de selección de malla de alambre para filtración de gases",
    de: "Auswahlmatrix für Drahtgewebe zur Gasfiltration",
    fr: "Matrice de sélection des toiles métalliques pour filtration de gaz",
    pt: "Matriz de seleção de malha de arame para filtração de gases"
  },
  matrixSubtitle: {
    en: "Standard mesh specifications for gas, steam, hot air, and process exhaust applications",
    zh: "适用于工业气体、工艺蒸汽、高温热空气及工业排气的标准金属丝网规格",
    es: "Especificaciones estándar de malla para gas, vapor, aire caliente y escape de procesos",
    de: "Standard-Gewebespezifikationen für Gas, Dampf, Heißluft und Prozessabgase",
    fr: "Spécifications de toiles standard pour gaz, vapeur, air chaud et effluents de procédé",
    pt: "Especificações padrão de malha para gás, vapor, ar quente e exaustão de processos"
  },
  matrixCtaResources: {
    en: "View Technical Resources",
    zh: "查看全部技术规格书",
    es: "Ver recursos técnicos",
    de: "Technische Ressourcen anzeigen",
    fr: "Voir les ressources techniques",
    pt: "Ver recursos técnicos"
  },
  thStructure: {
    en: "Weave Structure",
    zh: "编织与复合结构",
    es: "Estructura de tejido",
    de: "Gewebestruktur",
    fr: "Structure de tissage",
    pt: "Estrutura da trama"
  },
  thMicron: {
    en: "Micron Rating Range",
    zh: "微米截留等级",
    es: "Rango de micras",
    de: "Mikron-Bereich",
    fr: "Plage de seuil micronique",
    pt: "Faixa de classificação em mícrons"
  },
  thBenefit: {
    en: "Primary Performance Advantage",
    zh: "核心性能优势",
    es: "Ventaja principal de rendimiento",
    de: "Hauptleistungsvorteil",
    fr: "Avantage principal de performance",
    pt: "Principal vantagem de desempenho"
  },
  thDetails: {
    en: "Details",
    zh: "详细规格",
    es: "Detalles",
    de: "Details",
    fr: "Détails",
    pt: "Detalhes"
  },
  specLinkText: {
    en: "Specs",
    zh: "参数",
    es: "Especificaciones",
    de: "Spezifikationen",
    fr: "Spécifications",
    pt: "Especificações"
  }
};
export default function TechnicalFiltration() {
  const {
    lang
  } = useLanguage();
  const columns: DataTableColumn<SpecRow>[] = useMemo(() => [{
    key: "weaveType",
    header: pick(lang, copy.thStructure),
    cell: row => <span className="font-medium text-foreground">
            {pick(lang, row.weaveType)}
          </span>,
    sticky: true
  }, {
    key: "micronRating",
    header: pick(lang, copy.thMicron),
    align: "right",
    cell: row => <span className="font-mono text-sm text-foreground">
            {row.micronRating}
          </span>
  }, {
    key: "primaryBenefit",
    header: pick(lang, copy.thBenefit),
    cell: row => <span className="text-sm text-muted-foreground leading-relaxed">
            {pick(lang, row.primaryBenefit)}
          </span>
  }, {
    key: "details",
    header: pick(lang, copy.thDetails),
    align: "right",
    cell: row => <Link to={row.linkTo} className="inline-flex items-center text-sm font-semibold text-primary hover:underline gap-1">
            <span>{pick(lang, copy.specLinkText)}</span>
            <ArrowRight className="size-3.5" />
          </Link>
  }], [lang]);
  return <section id="gasfiltrationmatrix" data-nav="light" className="relative bg-background text-foreground mf-section border-b border-border overflow-hidden">
      <div className="mf-container">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="mf-h2 text-foreground font-default">
                {pick(lang, copy.matrixTitle)}
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-2 max-w-[65ch] mf-lead">
                {pick(lang, copy.matrixSubtitle)}
              </p>
            </div>
            <Button asChild variant="outline" size="sm" className="self-start md:self-auto">
              <Link to="/TechnicalResourcesPage">
                <FileSpreadsheet className="size-4 mr-2" />
                <span>{pick(lang, copy.matrixCtaResources)}</span>
              </Link>
            </Button>
          </div>
        </Reveal>

        <Reveal delay={80}>
          <DataTable<SpecRow> columns={columns} rows={technicalSpecs} getRowKey={row => row.linkTo} />
        </Reveal>
      </div>
    </section>;
}